import { Link } from 'react-router-dom';
import {
  Aperture,
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  ChartNoAxesCombined,
  FileUp,
  Layers,
  LockKeyhole,
  Plus,
  ShieldCheck,
  Sparkles,
  Users,
  WalletCards,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { TabIcon } from '@/components/icons';
import logo from '@/assets/sterling_logo.png';
import s from './Landing.module.css';

const FEATURES = [
  {
    icon: FileUp,
    number: '01',
    title: 'Bring the details together.',
    body: 'Import bank statements, record a quick purchase, or add a whole batch. Your accounts and transactions share one home.',
  },
  {
    icon: ChartNoAxesCombined,
    number: '02',
    title: 'Give every number context.',
    body: 'Follow your cash flow, plan your monthly budget, and see which categories and merchants shape your spending.',
  },
  {
    icon: Users,
    number: '03',
    title: 'Keep the shared stuff simple.',
    body: 'Split a payment, track what friends owe, and record repayments. Keep personal ventures alongside your market holdings.',
  },
];

function Wordmark() {
  return (
    <span className={s.brand}>
      <img src={logo} alt="" />
      <span>
        Sterling<span className={s.brandDot}>.</span>
      </span>
    </span>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const signedIn = Boolean(user);
  const ctaTo = signedIn ? '/app' : '/signin?mode=register';
  const currency = user?.currency ?? 'INR';
  const amount = (value: number) =>
    new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);

  return (
    <div className={s.page}>
      <a className={s.skipLink} href="#landing-content">
        Skip to content
      </a>
      <header className={s.header}>
        <Link to="/" aria-label="Sterling home">
          <Wordmark />
        </Link>
        <nav className={s.nav} aria-label="Sections">
          <a href="#features">The workspace</a>
          <a href="#advisor">Your advisor</a>
          <a href="#security">Privacy</a>
        </nav>
        <div className={s.headerActions}>
          {!signedIn && (
            <Link to="/signin" className={s.quietLink}>
              Sign in
            </Link>
          )}
          <Link to={ctaTo} className={s.cta}>
            {signedIn ? 'Open Sterling' : 'Get started'}{' '}
            <ArrowUpRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </header>

      <main id="landing-content" tabIndex={-1}>
        <section className={s.hero} aria-labelledby="landing-title">
          <div className={s.heroCopy}>
            <div className={s.eyebrow}>
              <span className={s.statusDot} /> A little perspective goes a long way
            </div>
            <h1 id="landing-title" className={s.h1}>
              Your money.
              <br />A <em>clearer</em> picture.
            </h1>
            <p className={s.lede}>
              A considered home for your everyday finances. Bring your accounts, spending, and plans
              together—with an advisor to help make sense of it all.
            </p>
            <div className={s.ctaRow}>
              <Link to={ctaTo} className={s.ctaLg}>
                {signedIn ? 'Back to your workspace' : 'Make room for clarity'}{' '}
                <ArrowUpRight size={18} aria-hidden="true" />
              </Link>
              <a href="#features" className={s.textLink}>
                Explore Sterling <ArrowRight size={16} aria-hidden="true" />
              </a>
            </div>
            <div className={s.heroNote}>
              <ShieldCheck size={16} aria-hidden="true" /> Your finances, with privacy in mind.
            </div>
          </div>

          <figure className={s.preview}>
            <div className={s.previewChrome}>
              <span className={s.windowDots} aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              <span>Your workspace, in perspective</span>
              <LockKeyhole size={12} aria-hidden="true" />
            </div>
            <div className={s.previewLayout}>
              <aside className={s.previewSidebar} aria-hidden="true">
                <img src={logo} alt="" />
                <span className={s.previewNavActive}>
                  <TabIcon name="overview" />
                </span>
                <span>
                  <TabIcon name="accounts" />
                </span>
                <span>
                  <TabIcon name="transactions" />
                </span>
                <span>
                  <TabIcon name="budgets" />
                </span>
                <span>
                  <TabIcon name="advisor" />
                </span>
              </aside>
              <div className={s.previewMain}>
                <div className={s.previewHeading}>
                  <span>
                    Overview<small>A good day to see the bigger picture.</small>
                  </span>
                  <span className={s.previewRecord}>
                    <Plus size={13} aria-hidden="true" /> Record
                  </span>
                </div>
                <div className={s.netWorthCard}>
                  <div className={s.cardCaption}>
                    <span>
                      <Aperture size={12} aria-hidden="true" /> THE BIG PICTURE
                    </span>
                    <span>Past 12 months</span>
                  </div>
                  <div className={s.netWorthLabel}>Your total net worth</div>
                  <div className={s.netWorth}>{amount(1284000)}</div>
                  <div className={s.deltaRow}>
                    <span>
                      <ArrowUpRight size={12} aria-hidden="true" /> +4.2%
                    </span>
                    <small>{amount(56930)} saved this month</small>
                  </div>
                  <svg
                    className={s.trend}
                    viewBox="0 0 480 100"
                    preserveAspectRatio="none"
                    role="img"
                    aria-label="Illustrative net worth trend rising over twelve months"
                  >
                    <defs>
                      <linearGradient id="landing-trend" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#69794c" stopOpacity="0.22" />
                        <stop offset="100%" stopColor="#69794c" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M0 81 C24 81 23 66 47 70 S78 87 100 65 S129 74 151 52 S184 67 209 42 S242 56 267 37 S300 47 324 24 S356 40 382 19 S416 30 440 13 S464 17 480 4 L480 100 L0 100 Z"
                      fill="url(#landing-trend)"
                    />
                    <path
                      className={s.trendLine}
                      d="M0 81 C24 81 23 66 47 70 S78 87 100 65 S129 74 151 52 S184 67 209 42 S242 56 267 37 S300 47 324 24 S356 40 382 19 S416 30 440 13 S464 17 480 4"
                      fill="none"
                      stroke="#647849"
                      strokeWidth="2.5"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                  <div className={s.trendLabels}>
                    <span>JAN</span>
                    <span>JUN</span>
                    <span>DEC</span>
                  </div>
                </div>
                <div className={s.previewBottom}>
                  <div className={s.budgetCard}>
                    <div className={s.miniTitle}>
                      <WalletCards size={13} aria-hidden="true" /> Monthly budget
                    </div>
                    <strong>
                      {amount(28400)} <small>/ {amount(40000)}</small>
                    </strong>
                    <div className={s.budgetTrack} aria-hidden="true">
                      <span />
                    </div>
                    <span className={s.budgetNote}>A little breathing room.</span>
                  </div>
                  <div className={s.insightCard}>
                    <div className={s.miniTitle}>
                      <Sparkles size={13} aria-hidden="true" /> A thought from your advisor
                    </div>
                    <p>
                      Small habits add up. See which recurring expenses are worth a second look.
                    </p>
                    <span>
                      See the details <ArrowUpRight size={12} aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <figcaption>
              Illustrative workspace <span>Sample amounts in {currency}</span>
            </figcaption>
          </figure>
        </section>

        <div className={s.capabilities} aria-label="Workspace features">
          <span>
            <Layers size={16} aria-hidden="true" /> Accounts, together
          </span>
          <span>
            <ArrowDownLeft size={16} aria-hidden="true" /> Spending, understood
          </span>
          <span>
            <WalletCards size={16} aria-hidden="true" /> Budgets, considered
          </span>
          <span>
            <Sparkles size={16} aria-hidden="true" /> Advice, in context
          </span>
        </div>

        <section id="features" className={s.features} aria-labelledby="features-title">
          <div className={s.sectionHead}>
            <div>
              <div className={s.eyebrow}>THE EVERYDAY, A LITTLE EASIER</div>
              <h2 id="features-title" className={s.h2}>
                Less keeping track.
                <br />
                More <em>moving forward.</em>
              </h2>
            </div>
            <p>
              From the coffee you just bought to the plans you’re working toward. One workspace for
              the whole picture.
            </p>
          </div>
          <div className={s.featureGrid}>
            {FEATURES.map(({ icon: Icon, number, title, body }) => (
              <article className={s.featureCard} key={number}>
                <div className={s.featureTop}>
                  <span className={s.featureIcon}>
                    <Icon size={21} aria-hidden="true" />
                  </span>
                  <span>{number}</span>
                </div>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="advisor" className={s.advisorSection} aria-labelledby="advisor-title">
          <div className={s.advisorCopy}>
            <div className={s.eyebrow}>
              <Sparkles size={15} aria-hidden="true" /> A SECOND PAIR OF EYES
            </div>
            <h2 id="advisor-title" className={s.h2}>
              Good questions.
              <br />
              <em>Grounded answers.</em>
            </h2>
            <p>
              What can I save? Where did the month go? Ask your advisor, and get a starting point
              drawn from your own finances.
            </p>
            <ul>
              <li>
                <Check size={16} aria-hidden="true" /> Insights based on your transactions
              </li>
              <li>
                <Check size={16} aria-hidden="true" /> A built-in advisor, with optional AI
                providers
              </li>
              <li>
                <Check size={16} aria-hidden="true" /> Preferences and guardrails you control
              </li>
            </ul>
          </div>
          <div className={s.conversation} aria-label="Illustrative advisor conversation">
            <div className={s.conversationHeader}>
              <span className={s.advisorAvatar}>
                <Sparkles size={18} aria-hidden="true" />
              </span>
              <span>
                Sterling Advisor<small>A little help with the bigger picture</small>
              </span>
              <span className={s.exampleBadge}>EXAMPLE</span>
            </div>
            <div className={s.question}>Where should I start with my budget?</div>
            <div className={s.answer}>
              Start with what’s already there. Review your recurring expenses, give your biggest
              spending categories a limit, and leave a buffer for the unexpected.
            </div>
            <div className={s.conversationFoot}>
              <span className={s.statusDot} /> Your next step, with a little more context.
            </div>
          </div>
        </section>

        <section id="security" className={s.privacy} aria-labelledby="privacy-title">
          <span className={s.privacyIcon}>
            <LockKeyhole size={27} aria-hidden="true" />
          </span>
          <div>
            <div className={s.eyebrow}>PERSONAL MEANS PERSONAL</div>
            <h2 id="privacy-title">Your finances deserve their own space.</h2>
            <p>
              Keep amounts hidden until you unlock them. Choose whether to connect an AI provider,
              and manage your devices from one place.
            </p>
          </div>
          <span className={s.privacyBadge}>
            <ShieldCheck size={16} aria-hidden="true" /> Private by design
          </span>
        </section>

        <section className={s.closing} aria-labelledby="closing-title">
          <div className={s.eyebrow}>A FRESH PERSPECTIVE</div>
          <h2 id="closing-title">
            Make sense of today.
            <br />
            <em>Make room for tomorrow.</em>
          </h2>
          <Link to={ctaTo} className={s.ctaLg}>
            {signedIn ? 'Open your workspace' : 'Get started with Sterling'}{' '}
            <ArrowUpRight size={18} aria-hidden="true" />
          </Link>
          <p>
            {signedIn
              ? 'Pick up right where you left off.'
              : 'A little clarity for your everyday finances.'}
          </p>
        </section>
      </main>

      <footer className={s.footer}>
        <div>
          <Link to="/" aria-label="Sterling home">
            <Wordmark />
          </Link>
          <p>Your money, in perspective.</p>
        </div>
        <nav className={s.footerLinks} aria-label="Footer">
          <a href="#features">The workspace</a>
          <a href="#security">Privacy</a>
          <Link to={signedIn ? '/app' : '/signin'}>
            {signedIn ? 'Dashboard' : 'Sign in'} <ArrowUpRight size={13} aria-hidden="true" />
          </Link>
        </nav>
        <span className={s.copyright}>© {new Date().getFullYear()} Cardinale Group</span>
      </footer>
    </div>
  );
}
